import type { MouseEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { DeleteOutlined, PaperClipOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { Breadcrumb, Button, Image } from 'antd';
import { FormProvider, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';

import { useCreateAboutBankMutation, useUpdateAboutBankMutation, type IAboutBank } from '@/api/endpoints/mainPages';
import { DataSection, ImageUploadField, InputField } from '@/shared/components';
import { ROUTES } from '@/shared/consts';
import { useApiRequest } from '@/shared/hooks';

import { ABOUT_BANK_VALIDATION_SCHEMA } from './consts';
import styles from './MainAboutBankPage.module.scss';
import type { TFormData } from './types';

export type TProp = {
  aboutBankData: IAboutBank | null;
  pageName: string;
  pageId: number;
};

export const MainAboutBankPageComponent = ({ aboutBankData, pageName, pageId }: TProp) => {
  const navigate = useNavigate();

  const { description, image } = aboutBankData ?? {};

  const sendApiRequest = useApiRequest();

  const [createAboutBank, { isLoading: isAboutBankCreating }] = useCreateAboutBankMutation();
  const [updateAboutBank, { isLoading: isAboutBankUpdating }] = useUpdateAboutBankMutation();

  const methods = useForm<TFormData>({
    defaultValues: {
      description: description ?? '',
      image: image ?? null,
    },
    resolver: yupResolver(ABOUT_BANK_VALIDATION_SCHEMA),
  });

  const [isImageChanged, setIsImageChanged] = useState(false);
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState('');

  const { watch, handleSubmit, setValue } = methods;

  const currentImage = watch('image');

  const getImageName = () => {
    if (!currentImage) {
      return '';
    }

    return currentImage.filename;
  };

  const removeUploadedImage = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    setValue('image', null);
    setImageUrl('');
    setIsImageChanged(false);
  };

  const imageRequestCallback = (file: File) => {
    const url = URL.createObjectURL(file);
    setImageUrl(url);
  };

  const imageChangeCallback = useCallback(() => setIsImageChanged(true), []);

  const goBack = () => {
    navigate(`/${ROUTES.PUBLIC_PAGES}/${ROUTES.MAIN}/${pageId}`);
  };

  const saveData = handleSubmit(({ description, image }) => {
    sendApiRequest({
      successMessage: 'Данные успешно сохранены',
      apiRequestCallback: () => {
        const apiRequestCallback = aboutBankData ? updateAboutBank : createAboutBank;

        return apiRequestCallback({
          id: pageId,
          data: {
            description,
            ...(image?.signed_id && {
              image: image.signed_id,
            }),
          },
        });
      },
    });
  });

  useEffect(() => {
    if (!currentImage?.url) {
      return;
    }

    if (!isImageChanged) {
      setImageUrl(currentImage.url);
    }
  }, [isImageChanged, currentImage]);

  return (
    <div className={styles.wrapper}>
      <Breadcrumb
        items={[
          { title: 'Публичные страницы' },
          { title: <Link to={`/${ROUTES.PUBLIC_PAGES}/${ROUTES.MAIN}`}>Главная</Link> },
          { title: <Link to={`/${ROUTES.PUBLIC_PAGES}/${ROUTES.MAIN}/${pageId}`}>{pageName}</Link> },
          { title: 'О ПСБ' },
        ]}
      />
      <div className={styles['content-wrapper']}>
        <FormProvider {...methods}>
          <DataSection
            titleText="Текст блока"
            isTitleRequired
          >
            <InputField
              fieldName="description"
              placeholder="Текст блока"
              shouldEnableLengthCounter
              maxLengthCount={500}
            />
          </DataSection>
          <DataSection
            titleText="Фоновое изображение"
            isTitleRequired
          >
            <ImageUploadField
              accept="image/png,image/jpeg,image/jpg"
              name="Фоновое изображение"
              fieldName="image"
              className={currentImage ? styles['image-uploaded'] : ''}
              requestCallback={imageRequestCallback}
              changeCallback={imageChangeCallback}
              showUploadList={false}
            />
            {currentImage && (
              <>
                <Button
                  size="small"
                  onClick={() => setIsImagePreviewOpen(true)}
                  type="text"
                  icon={<PaperClipOutlined />}
                  className={styles['image-button-wrapper']}
                >
                  {getImageName()}
                  <DeleteOutlined
                    className={styles['remove-image-icon']}
                    onClick={removeUploadedImage}
                  />
                </Button>
                <Image
                  alt="Фоновое изображение"
                  style={{ display: 'none' }}
                  preview={{
                    open: isImagePreviewOpen,
                    src: imageUrl,
                    onOpenChange: (value) => setIsImagePreviewOpen(value),
                  }}
                />
              </>
            )}
          </DataSection>
          <div className={styles['buttons-wrapper']}>
            <Button
              onClick={goBack}
              type="dashed"
              size="large"
              className={styles.button}
            >
              Назад
            </Button>
            <Button
              onClick={saveData}
              type="primary"
              size="large"
              className={styles.button}
              disabled={isAboutBankCreating || isAboutBankUpdating}
            >
              Сохранить
            </Button>
          </div>
        </FormProvider>
      </div>
    </div>
  );
};
